import { type ChangeEvent, type FC } from 'react';

import { InputPhone } from 'components/input/InputPhone';
import { InputTitle } from 'components/input/InputTitle';
import CommonSelect from 'components/select/CustomSelect';
import {
  type DropdownMenuItem,
  type InputColor,
  Radio,
  Row,
  Select,
  Spacing,
  TextInput,
  Typography,
} from 'enable-ui';
import { GenderType } from 'enums/gender';

import { RoleKey } from 'modules/auth/core/enum';
import { CollapseIcon } from 'modules/classes/styles/member';

import { SelectStyle } from 'styles/select';

import { ReactComponent as DownIcon } from 'assets/icons/collapse-down.svg';
import { ReactComponent as UpIcon } from 'assets/icons/up.svg';

import type { LearnerFormRequest, UserMeasurement } from '../core/type';

import { checkRole, getDateOfBirth, showMonth } from 'helpers';
import { useBoolean } from 'hooks';
import { isUndefined } from 'lodash';

import { COLOR, COLOR_CONFIG } from 'constants/color';

import { isWrongAge, renderDateOfBirthError, renderMeasurementError } from '../utils';
import { LearnerPasswordForm } from './LearnerPasswordForm';

const textInputStyles = {
  containerStyle: { maxWidth: 'auto' },
  inputStyle: { borderRadius: 12 },
};

const selectStyles = {
  inputStyle: { maxWidth: 'auto', borderRadius: 12 },
  containerStyle: { maxWidth: 'auto' },
};

const dateOfBirthTextError = {
  whiteSpace: 'nowrap',
  color: COLOR_CONFIG.red,
  paddingTop: 6,
};

export const LearnerProfileForm: FC<
  Omit<LearnerFormRequest, 'password' | 'nickname'> & {
    learnerId?: string;
    nickname: string;
    password?: string;
    handleChange: any;
    handleChangeMeasurement?: (type: keyof UserMeasurement, value: string) => void;
    handleChangePhone: (value: string) => void;
    onChangeGender: (newGenderKey: string) => () => void;
    emailStatus: {
      color: InputColor;
      helperText: string;
    };
    handleChangeDate: (type: keyof LearnerFormRequest, value: number) => void;
    disabledField?: boolean;
    disabledMeasurement?: boolean;
    isAccount?: {
      value: boolean;
      setValue: (value: boolean) => void;
    };
    onChangeMonths?: (selectedOption: number) => void;
    searchSelectMonth?: string;
    onChangeSearchMonths?: (selectedOption: string) => void;
    searchSelectYear?: string;
    onChangeYears?: (selectedOption: string) => void;
    onChangeSearchYears?: (selectedOption: string) => void;
  }
> = ({
  learnerId,
  name,
  firstName,
  lastName,
  gender,
  phone,
  dayOfBirth,
  monthOfBirth,
  yearOfBirth,
  email,
  height,
  weight,
  bodyFat,
  restingMetabolism,
  skeletalMuscle,
  visceralFatLevel,
  handleChange,
  onChangeGender,
  handleChangeMeasurement,
  handleChangePhone,
  emailStatus,
  handleChangeDate,
  disabledField,
  disabledMeasurement,
  isAccount,
  password,
  onChangeMonths = () => {},
  searchSelectMonth = '',
  onChangeSearchMonths = () => {},
  searchSelectYear = '',
  onChangeYears = () => {},
  onChangeSearchYears = () => {},
}) => {
  const { months, years } = getDateOfBirth();

  const isCollapse = useBoolean(false);

  // const dayErrorMessage = renderDateOfBirthError(
  //   'dayOfBirth',
  //   dayOfBirth,
  //   monthOfBirth,
  //   yearOfBirth,
  // );
  // const monthErrorMessage = renderDateOfBirthError(
  //   'monthOfBirth',
  //   dayOfBirth,
  //   monthOfBirth,
  //   yearOfBirth,
  // );
  const yearErrorMessage = renderDateOfBirthError(
    'yearOfBirth',
    dayOfBirth,
    monthOfBirth,
    yearOfBirth,
  );

  const handleKeyPress = (e: any) => {
    const keyCode = e.keyCode || e.which;
    const keyValue = String.fromCharCode(keyCode);

    if ((e.target.value === '' && keyValue === '0') || keyValue === '-') {
      e.preventDefault();
    }
  };

  const onChangeDateOfBirth =
    (type: keyof LearnerFormRequest) =>
    (selected: DropdownMenuItem | DropdownMenuItem[] | null) => {
      if (!selected) {
        return;
      }

      handleChangeDate(type, Number((selected as DropdownMenuItem).key));
    };

  const onChangeMeasurement = (event: ChangeEvent<HTMLInputElement>) => {
    handleChangeMeasurement?.(event.target.name as keyof UserMeasurement, event.target.value);
  };

  const bmi =
    weight && height
      ? Number(((Number(weight) * 10000) / (Number(height) * Number(height))).toFixed(2))
      : undefined;

  const isTrainer = checkRole([RoleKey.TRAINER_BIOLOGIC, RoleKey.TRAINER_INSTITUTION]);

  const menuMonths = months.map((m, index) => ({
    value: String(index),
    label: String(m),
  }));

  const menuYears = years.map((y) => {
    return {
      value: String(y),
      label: y === -1 ? 'Not select' : String(y),
    };
  });

  return (
    <Spacing
      spacing="md"
      type="margin"
      placement="top"
      style={{
        width: 800,
        marginTop: '16px',
      }}
    >
      <Row direction="column" style={{ gap: 24 }}>
        {/* <Typography variant="h4" weight={600}>
          Profile
        </Typography> */}
        {/* {!learnerId && (
          <Spacing
            spacing="sm"
            placement="top"
            type="margin"
            style={{
              width: '100%',
            }}
          >
            <InputTitle title="Nick name" required />
            <TextInput
              name="nickname"
              containerStyle={{ maxWidth: 'auto' }}
              value={nickname}
              onChange={handleChange}
              maxLength={80}
            />
          </Spacing>
        )} */}

        <Row
          style={{
            gap: 12,
          }}
        >
          <div style={{ width: '50%' }}>
            <Row direction="column">
              <InputTitle title="Given name" />
              <TextInput
                name="firstName"
                {...textInputStyles}
                value={firstName}
                onChange={handleChange}
                disabled={disabledField}
              />
            </Row>
          </div>
          <div style={{ width: '50%' }}>
            <Row direction="column">
              <InputTitle title="Family name" />
              <TextInput
                name="lastName"
                {...textInputStyles}
                value={lastName}
                onChange={handleChange}
                disabled={disabledField}
              />
            </Row>
          </div>
        </Row>
        {!isTrainer && (
          <InputPhone title="Phone number" value={phone} onChange={handleChangePhone} />
        )}

        <Row direction="column">
          <InputTitle title="Gender" />
          <Row>
            {Object.keys(GenderType).map((key) => {
              const checked = gender?.toLowerCase() === key.toLowerCase();

              return (
                <Radio
                  key={key}
                  style={{
                    marginRight: 10,
                    fontSize: 16,
                    lineHeight: '22px',
                    color: COLOR.surfaceNeutralBold['neutral-11'],
                  }}
                  checked={checked}
                  onClick={onChangeGender(key)}
                  // disabled={disabledField}
                >
                  {key}
                </Radio>
              );
            })}
          </Row>
        </Row>

        <Row
          style={{
            gap: 12,
            gridTemplateColumns: 'repeat(3, 1fr)',
            display: 'grid',
          }}
        >
          <div>
            <InputTitle title="Age" />
            <TextInput
              name="age"
              {...textInputStyles}
              type="number"
              value={
                yearOfBirth && Number(yearOfBirth) > 0 ? new Date().getFullYear() - yearOfBirth : ''
              }
              min={1}
              onChange={(e) =>
                handleChangeDate(
                  'yearOfBirth',
                  e.target.value ? new Date().getFullYear() - parseInt(e.target.value) : -1,
                )
              }
              helperText={
                yearOfBirth && isWrongAge(yearOfBirth) ? 'Please enter between 1 and 100' : ''
              }
            />
          </div>
          <div>
            <InputTitle title="Month (optional)" />
            <CommonSelect
              value={menuMonths.find((item: any) => item.value === String(monthOfBirth)) || null}
              options={menuMonths.filter((ele) =>
                ele.label.toLowerCase().includes(searchSelectMonth.toLowerCase()),
              )}
              onChange={onChangeMonths}
              onInputChange={onChangeSearchMonths}
              noOptionsMessage={() => 'Not Found Month'}
              isBorderInput
            />
            {/* {monthErrorMessage ? (
                  <InputTitle title={monthErrorMessage} style={{ ...dateOfBirthTextError }} />
                ) : null} */}
          </div>

          <div>
            <InputTitle title="Year" />
            <CommonSelect
              value={menuYears.find((item: any) => item.value === String(yearOfBirth)) || null}
              options={menuYears.filter((ele) =>
                ele.label.toLowerCase().includes(searchSelectYear.toLowerCase()),
              )}
              onChange={onChangeYears}
              onInputChange={onChangeSearchYears}
              noOptionsMessage={() => 'Not Found Year'}
              isBorderInput
            />
            {yearErrorMessage ? (
              <InputTitle title={yearErrorMessage} style={{ ...dateOfBirthTextError }} />
            ) : null}
          </div>
        </Row>

        <div
          style={{ display: 'flex', alignItems: 'center', gap: 4, height: 44, cursor: 'pointer' }}
          onClick={() => isCollapse.setValue(!isCollapse.value)}
        >
          <Typography color={COLOR.purpel['purpel-6']} variant="body-2">
            Optional information
          </Typography>

          <CollapseIcon>{isCollapse.value ? <UpIcon /> : <DownIcon />}</CollapseIcon>
        </div>
        {isCollapse.value && (
          <>
            {!isTrainer && (
              <Row direction="column">
                <InputTitle title="Email" />
                <TextInput
                  name="email"
                  {...textInputStyles}
                  value={email}
                  onChange={handleChange}
                  helperText={emailStatus.helperText}
                  color={emailStatus.color}
                  disabled={disabledField}
                />
              </Row>
            )}

            <Row
              style={{
                gap: 12,
              }}
            >
              <div style={{ width: '25%' }}>
                <InputTitle title="Height" />
                <TextInput
                  name="height"
                  {...textInputStyles}
                  type="number"
                  value={height ?? ''}
                  onChange={onChangeMeasurement}
                  suffix="cm"
                  // disabled={disabledField}
                  onKeyPress={handleKeyPress}
                  min={1}
                  helperText={renderMeasurementError('height', String(height))}
                />
              </div>
              <div style={{ width: '25%' }}>
                <InputTitle title="Weight" />
                <TextInput
                  name="weight"
                  {...textInputStyles}
                  type="number"
                  value={weight ?? ''}
                  onChange={onChangeMeasurement}
                  suffix="kg"
                  // disabled={disabledField}
                  onKeyPress={handleKeyPress}
                  min={1}
                  helperText={renderMeasurementError('weight', String(weight))}
                />
              </div>

              <div style={{ width: '50%' }}>
                <InputTitle title="BMI" />
                <TextInput name="bmi" {...textInputStyles} type="number" disabled value={bmi} />
              </div>
            </Row>
            <Row
              style={{
                gap: 12,
              }}
            >
              <div style={{ width: '25%' }}>
                <InputTitle title="Body fat" />
                <TextInput
                  name="bodyFat"
                  {...textInputStyles}
                  type="number"
                  value={bodyFat ?? ''}
                  onChange={handleChange}
                  suffix="%"
                  // disabled={disabledField || disabledMeasurement}
                  onKeyPress={handleKeyPress}
                  min={1}
                  helperText={renderMeasurementError('bodyFat', String(bodyFat))}
                />
              </div>
              <div style={{ width: '25%' }}>
                <InputTitle title="Visceral Fats Level" />
                <TextInput
                  name="visceralFatLevel"
                  {...textInputStyles}
                  type="number"
                  value={visceralFatLevel ?? ''}
                  onChange={handleChange}
                  // disabled={disabledField || disabledMeasurement}
                  onKeyPress={handleKeyPress}
                  min={1}
                  helperText={renderMeasurementError('visceralFatLevel', String(visceralFatLevel))}
                />
              </div>
              <div style={{ width: '25%' }}>
                <InputTitle title="Skeletal muscle" />
                <TextInput
                  name="skeletalMuscle"
                  {...textInputStyles}
                  type="number"
                  value={skeletalMuscle ?? ''}
                  onChange={handleChange}
                  suffix="%"
                  // disabled={disabledField || disabledMeasurement}
                  onKeyPress={handleKeyPress}
                  min={1}
                  helperText={renderMeasurementError('skeletalMuscle', String(skeletalMuscle))}
                />
              </div>
              <div style={{ width: '25%' }}>
                <InputTitle title="Resting Metabolism" />
                <TextInput
                  name="restingMetabolism"
                  {...textInputStyles}
                  type="number"
                  value={restingMetabolism ?? ''}
                  onChange={handleChange}
                  suffix="kcal"
                  // disabled={disabledField || disabledMeasurement}
                  onKeyPress={handleKeyPress}
                  min={1}
                  helperText={renderMeasurementError(
                    'restingMetabolism',
                    String(restingMetabolism),
                  )}
                />
              </div>
            </Row>
            {!isUndefined(isAccount) && (
              <LearnerPasswordForm
                password={password || ''}
                handleChange={handleChange}
                isAccount={isAccount}
              />
            )}
          </>
        )}
      </Row>
    </Spacing>
  );
};
